/**
 * @fileoverview Shared error handling for VisualDX: logging and optional UI feedback.
 */

/** CSS class applied to elements in error state (stylesheet defines look). */
const ERROR_CLASS = 'vdx-error';

/** Recognised vdx-* attribute names (exact). Unrecognised attributes may be typos and get error styling. */
const KNOWN_VDX_ATTRIBUTES = new Set([
    'vdx-accordian', 'vdx-back-button', 'vdx-breadcrumbs', 'vdx-child-hover', 'vdx-copy-alert', 'vdx-merge-here', 'vdx-merge-this', 'vdx-copy-button', 'vdx-copy-this',
    'vdx-count-ease', 'vdx-count-up', 'vdx-countdown-days', 'vdx-countdown-enddate', 'vdx-countdown-hours', 'vdx-countdown-mins', 'vdx-countdown-secs', 'vdx-countdown-startdate',
    'vdx-date-currentdate', 'vdx-date-date', 'vdx-date-day', 'vdx-date-lang', 'vdx-date-month', 'vdx-date-year',
    'vdx-dropdown-open', 'vdx-elapsed-time', 'vdx-filters-form', 'vdx-filters-items', 'vdx-filters-search',
    'vdx-footnotes-article', 'vdx-footnotes-footnotes', 'vdx-footnotes-link',
    'vdx-gsap-countup', 'vdx-gsap-duration', 'vdx-gsap-ease', 'vdx-gsap-horizontalscroll', 'vdx-gsap-linktext', 'vdx-gsap-splittext', 'vdx-gsap-stagger', 'vdx-gsap-staggerin', 'vdx-gsap-typewriter',
    'vdx-mirrorclick-button', 'vdx-mirrorclick-this', 'vdx-nest-link', 'vdx-nest-this', 'vdx-nth-child',
    'vdx-on-click', 'vdx-password-input', 'vdx-password-toggle',
    'vdx-popup-close', 'vdx-popup-focus', 'vdx-popup-frequency', 'vdx-popup-onexit', 'vdx-popup-open', 'vdx-popup-popup', 'vdx-popup-scrolldown', 'vdx-popup-scrollup',
    'vdx-readtime-article', 'vdx-readtime-link', 'vdx-readtime-time', 'vdx-redirect-url', 'vdx-remove-element',
    'vdx-share-alert', 'vdx-share-email', 'vdx-share-facebook', 'vdx-share-linkedin', 'vdx-share-page', 'vdx-share-twitter', 'vdx-share-whatsapp',
    'vdx-shownav-nav', 'vdx-shownav-scrollbackup', 'vdx-shownav-scrolldown', 'vdx-shownav-triggertop',
    'vdx-slider-autorotate', 'vdx-slider-clone', 'vdx-slider-dot', 'vdx-slider-nav', 'vdx-slider-next', 'vdx-slider-prev', 'vdx-slider-slide', 'vdx-slider-slider', 'vdx-slider-thumbnail',
    'vdx-sort-button', 'vdx-sort-random', 'vdx-scroll-offset', 'vdx-tabs-button', 'vdx-tabs-content', 'vdx-tabs-pause', 'vdx-tabs-pane', 'vdx-tabs-tab', 'vdx-tabs-timed',
    'vdx-time-clock', 'vdx-time-currenttime', 'vdx-time-display', 'vdx-time-hour', 'vdx-time-min', 'vdx-time-seconds', 'vdx-time-timezone',
    'vdx-toast-button', 'vdx-toast-delay', 'vdx-toast-message', 'vdx-toc-article', 'vdx-toc-list', 'vdx-tooltip-bottom', 'vdx-tooltip-left', 'vdx-tooltip-right', 'vdx-tooltip-top',
    'vdx-transition-delay', 'vdx-url-param',
]);

/** Prefixes for vdx-* attributes that accept any suffix (e.g. vdx-toc-h1, vdx-param-foo). */
const KNOWN_VDX_PREFIXES = ['vdx-toc-', 'vdx-param-'];

function isKnownVdxAttribute(name) {
    if (typeof name !== 'string' || !name.startsWith('vdx-')) return false;
    if (KNOWN_VDX_ATTRIBUTES.has(name)) return true;
    return KNOWN_VDX_PREFIXES.some((p) => name.startsWith(p));
}

/** Id of the injected style tag so we only inject once. */
const STYLE_ID = 'vdx-errors-styles';

/** Hostname suffix for Webflow preview/published domains – visual errors only show here. */
const WEBFLOW_DOMAIN_SUFFIX = '.webflow.io';

/**
 * True when the current origin is a Webflow domain (*.webflow.io). Visual error state (showErrorOnElement) only runs on these domains.
 * @returns {boolean}
 */
function isWebflowDomain() {
    if (typeof window === 'undefined' || !window.location || !window.location.hostname) return false;
    return window.location.hostname.endsWith(WEBFLOW_DOMAIN_SUFFIX);
}

/** Default styles for .vdx-error – injected once so pages don't need their own CSS. */
const ERROR_STYLES = `.${ERROR_CLASS}{border:1px dotted #ef4444 !important;cursor:help;}`;

let stylesInjected = false;

/**
 * Injects global .vdx-error styles into the document so error state is visible without page CSS.
 */
function injectErrorStyles() {
    if (typeof document === 'undefined' || !isWebflowDomain()) return;
    if (document.getElementById(STYLE_ID)) {
        stylesInjected = true;
        return;
    }
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = ERROR_STYLES;
    (document.head || document.documentElement).appendChild(style);
    stylesInjected = true;
}

/**
 * Logs an error with optional module context.
 * @param {string} message
 * @param {{ module?: string, error?: unknown, context?: Record<string, unknown> }} [options]
 */
export function logError(message, options = {}) {
    const { module, error, context } = options;
    const prefix = module ? `[vdx/${module}]` : '[vdx]';
    console.error(prefix, message, error != null ? error : '', context ?? '');
}

/**
 * Logs a warning with optional module context.
 * @param {string} message
 * @param {{ module?: string, context?: Record<string, unknown> }} [options]
 */
export function logWarn(message, options = {}) {
    const { module, context } = options;
    const prefix = module ? `[vdx/${module}]` : '[vdx]';
    console.warn(prefix, message, context ?? '');
}

/**
 * Logs an info message with optional module context.
 * @param {string} message
 * @param {{ module?: string, context?: Record<string, unknown> }} [options]
 */
export function logInfo(message, options = {}) {
    const { module, context } = options;
    const prefix = module ? `[vdx/${module}]` : '[vdx]';
    console.log(prefix, message, context ?? '');
}

/**
 * Shows error state on an element (class + title/aria). Styling lives in CSS.
 * @param {Element} element
 * @param {string} message
 * @param {{ class: string } | void} [options]
 */
export function showErrorOnElement(element, message, options = {}) {
    if (!element || typeof element.classList === 'undefined') return;
    if (!isWebflowDomain()) return;
    if (!stylesInjected) injectErrorStyles();
    const errorClass = options.class ?? ERROR_CLASS;
    element.setAttribute('title', message);
    element.setAttribute('aria-invalid', 'true');
    element.classList.add(errorClass);
}

/**
 * Removes error state from an element.
 * @param {Element} element
 * @param {{ class: string } | void} [options]
 */
export function clearErrorOnElement(element, options = {}) {
    if (!element || typeof element.classList === 'undefined') return;
    const errorClass = options.class ?? ERROR_CLASS;
    element.removeAttribute('title');
    element.removeAttribute('aria-invalid');
    element.classList.remove(errorClass);
}

/**
 * Logs the error and optionally shows it on an element.
 * @param {string} message
 * @param {{
 *   module?: string;
 *   error?: unknown;
 *   element?: Element | null;
 *   showOnElement?: boolean;
 *   errorClass?: string;
 * }} [options]
 */
export function handleError(message, options = {}) {
    const { module = 'app', error, element, showOnElement, errorClass } = options;
    logError(message, { module, error });
    if (showOnElement && element) {
        showErrorOnElement(element, message, errorClass ? { class: errorClass } : undefined);
    }
}

/**
 * Scans the document for elements with vdx-* attributes that are not in the recognised list.
 * On Webflow domains, injects error styles and shows error state on those elements (possible typo).
 * Always logs a warning per unrecognised attribute.
 */
export function checkUnrecognisedVdxAttributes() {
    if (typeof document === 'undefined' || !document.body) return;
    const elements = document.body.querySelectorAll('*');
    elements.forEach((el) => {
        Array.from(el.attributes).forEach((attr) => {
            if (!attr.name.startsWith('vdx-')) return;
            if (isKnownVdxAttribute(attr.name)) return;
            const msg = `Unrecognised attribute [${attr.name}]. Possible typo? Check spelling against VisualDX docs.`;
            logWarn(msg, { module: 'attributes', context: { attribute: attr.name } });
            showErrorOnElement(el, msg);
        });
    });
}
